import { Link } from 'react-router-dom';
import { Monitor, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LyrraOneLogo } from '@/components/brand/LyrraOneLogo';
import { isLyrraOne } from '@/lib/platform';
import {
  getMobileFallbackPath,
  getMobileTrainerSalesPath,
} from '@/lib/mobile-workspace-access';
import { canAccessTrainerDashboard } from '@/lib/auth-role';
import { useAuthStore } from '@/stores/auth.store';

type DesktopOnlyGateVariant = 'admin' | 'trainer';

export function DesktopOnlyGate({ variant }: { variant: DesktopOnlyGateVariant }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const learnerHref = getMobileFallbackPath(user?.role);
  const salesHref = getMobileTrainerSalesPath();
  const showSalesLink = variant === 'trainer' && canAccessTrainerDashboard(user?.role) && isLyrraOne();

  const title = variant === 'admin'
    ? t('mobileGate.adminTitle', 'Administration indisponible sur mobile')
    : t('mobileGate.trainerTitle', 'Section réservée à l’ordinateur');

  const description = variant === 'admin'
    ? t(
        'mobileGate.adminDescription',
        'La gestion de la plateforme nécessite un écran d’au moins 768 px de large. Connectez-vous depuis un ordinateur ou une tablette en mode paysage.',
      )
    : t(
        'mobileGate.trainerDescription',
        'La création de formations, le marketing et les autres outils formateur nécessitent un ordinateur. Sur smartphone, seul le tableau de bord des ventes est disponible.',
      );

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#F8FAFC] px-4 py-10 text-center dark:bg-[hsl(222,47%,6%)]">
      <div className="w-full max-w-md rounded-2xl border border-[#E8EDF5] bg-white p-8 shadow-lg dark:border-[hsl(217,33%,22%)] dark:bg-[hsl(217,33%,12%)]">
        {isLyrraOne() ? (
          <LyrraOneLogo size="md" linkTo={false} className="mx-auto mb-6" />
        ) : null}
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EFF6FF] text-[#2563EB] dark:bg-[#1e3a5f]">
          <Monitor className="h-7 w-7" aria-hidden />
        </div>
        <div className="mx-auto mb-4 flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-wide text-[#64748B]">
          <Smartphone className="h-4 w-4" aria-hidden />
          <span>{t('mobileGate.badge', 'Smartphone détecté')}</span>
        </div>
        <h1 className="text-xl font-bold text-[#0F172A] dark:text-white">{title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#64748B] dark:text-[hsl(215,20%,72%)]">
          {description}
        </p>
        <div className="mt-6 flex flex-col gap-2">
          {showSalesLink ? (
            <Link
              to={salesHref}
              className="inline-flex min-h-[2.75rem] w-full items-center justify-center rounded-xl bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8]"
            >
              {t('mobileGate.goToSales', 'Voir mes ventes')}
            </Link>
          ) : null}
          <Link
            to={learnerHref}
            className={`inline-flex min-h-[2.75rem] w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
              showSalesLink
                ? 'border border-[#E8EDF5] text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[hsl(217,33%,22%)] dark:text-white dark:hover:bg-[hsl(217,33%,17%)]'
                : 'bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8]'
            }`}
          >
            {t('mobileGate.goToLearner', 'Aller à mon espace apprenant')}
          </Link>
        </div>
      </div>
    </div>
  );
}
